import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

import notificationsAPI, {
  type NotificationListParams,
  type NotificationPage,
  type NotificationUnreadCount
} from '@global-apis/notifications';
import type Notification from '@global-interfaces/Notification';
import { queryKeys, staleTimes } from '@root/constants/queryKeys';

type NotificationQueryOptions<T> = Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn' | 'staleTime'>;
type NotificationListCache = NotificationPage<Notification> | InfiniteData<NotificationPage<Notification>, number>;

const isInfiniteNotificationCache = (
  cache: NotificationListCache
): cache is InfiniteData<NotificationPage<Notification>, number> => 'pages' in cache;

const getNotificationPages = (cache: NotificationListCache) =>
  isInfiniteNotificationCache(cache) ? cache.pages : [cache];

const mapNotificationPages = (
  cache: NotificationListCache,
  mapPage: (page: NotificationPage<Notification>) => NotificationPage<Notification>
): NotificationListCache =>
  isInfiniteNotificationCache(cache) ? { ...cache, pages: cache.pages.map(mapPage) } : mapPage(cache);

const invalidateNotificationQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications }),
    queryClient.invalidateQueries({ queryKey: queryKeys.notificationsUnreadCount })
  ]);

interface NotificationCacheSnapshot {
  lists: [QueryKey, NotificationListCache | undefined][];
  unreadCount: NotificationUnreadCount | undefined;
}

interface MarkOneNotificationContext extends NotificationCacheSnapshot {
  optimisticReadAt: string;
  wasUnread: boolean;
}

const cancelNotificationQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.cancelQueries({ queryKey: queryKeys.notifications }),
    queryClient.cancelQueries({ queryKey: queryKeys.notificationsUnreadCount })
  ]);

const snapshotNotificationCache = (queryClient: QueryClient): NotificationCacheSnapshot => ({
  lists: queryClient.getQueriesData<NotificationListCache>({ queryKey: queryKeys.notifications }),
  unreadCount: queryClient.getQueryData<NotificationUnreadCount>(queryKeys.notificationsUnreadCount)
});

const restoreNotificationCache = (queryClient: QueryClient, snapshot?: NotificationCacheSnapshot) => {
  if (!snapshot) {
    return;
  }
  snapshot.lists.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
  queryClient.setQueryData(queryKeys.notificationsUnreadCount, snapshot.unreadCount);
};

const restoreOneNotificationCache = (
  queryClient: QueryClient,
  notificationId: number,
  context?: MarkOneNotificationContext
) => {
  if (!context) {
    return;
  }

  let restored = false;
  context.lists.forEach(([queryKey, previousCache]) => {
    const previousNotification = previousCache
      ? getNotificationPages(previousCache)
          .flatMap(({ results }) => results)
          .find(({ id }) => id === notificationId)
      : undefined;
    if (!previousNotification) {
      return;
    }
    queryClient.setQueryData<NotificationListCache>(queryKey, (currentCache) => {
      if (!currentCache) {
        return currentCache;
      }
      return mapNotificationPages(currentCache, (page) => ({
        ...page,
        results: page.results.map((notification) => {
          if (notification.id !== notificationId || notification.read_at !== context.optimisticReadAt) {
            return notification;
          }
          restored = true;
          return { ...notification, read_at: previousNotification.read_at };
        })
      }));
    });
  });

  if (restored && context.wasUnread) {
    queryClient.setQueryData<NotificationUnreadCount>(queryKeys.notificationsUnreadCount, (currentCount) =>
      currentCount ? { unread: currentCount.unread + 1 } : context.unreadCount
    );
  }
};

const optimisticallyMarkOneRead = (
  queryClient: QueryClient,
  notificationId: number,
  readAt: string,
  snapshot: NotificationCacheSnapshot
) => {
  const wasUnread = snapshot.lists.some(([, cache]) =>
    cache
      ? getNotificationPages(cache).some(({ results }) =>
          results.some((notification) => notification.id === notificationId && !notification.read_at)
        )
      : false
  );
  queryClient.setQueriesData<NotificationListCache>({ queryKey: queryKeys.notifications }, (cache) =>
    cache
      ? mapNotificationPages(cache, (page) => ({
          ...page,
          results: page.results.map((notification) =>
            notification.id === notificationId ? { ...notification, read_at: readAt } : notification
          )
        }))
      : cache
  );
  if (wasUnread) {
    queryClient.setQueryData<NotificationUnreadCount>(queryKeys.notificationsUnreadCount, (count) =>
      count ? { unread: Math.max(0, count.unread - 1) } : count
    );
  }
  return wasUnread;
};

const optimisticallyMarkAllRead = (queryClient: QueryClient, readAt: string) => {
  queryClient.setQueriesData<NotificationListCache>({ queryKey: queryKeys.notifications }, (cache) =>
    cache
      ? mapNotificationPages(cache, (page) => ({
          ...page,
          results: page.results.map((notification) => ({
            ...notification,
            read_at: notification.read_at ?? readAt
          }))
        }))
      : cache
  );
  queryClient.setQueryData<NotificationUnreadCount>(queryKeys.notificationsUnreadCount, (count) =>
    count ? { unread: 0 } : count
  );
};

export const useNotifications = (
  params: NotificationListParams = {},
  options: NotificationQueryOptions<NotificationPage<Notification>> = {}
) =>
  useQuery<NotificationPage<Notification>, Error>({
    ...options,
    queryKey: [...queryKeys.notifications, params],
    queryFn: () => notificationsAPI.list(params),
    staleTime: staleTimes.realtime
  });

export const useInfiniteNotifications = (
  params: Omit<NotificationListParams, 'page'> = {},
  options: { refetchInterval?: number | false } = {}
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [...queryKeys.notifications, 'infinite', params],
    queryFn: ({ pageParam }) => notificationsAPI.list({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    staleTime: staleTimes.realtime
  });

export const useNotificationUnreadCount = (options: NotificationQueryOptions<NotificationUnreadCount> = {}) =>
  useQuery<NotificationUnreadCount, Error>({
    ...options,
    queryKey: queryKeys.notificationsUnreadCount,
    queryFn: notificationsAPI.unreadCount,
    staleTime: staleTimes.realtime
  });

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsAPI.markRead,
    onMutate: async (notificationId) => {
      await cancelNotificationQueries(queryClient);
      const snapshot = snapshotNotificationCache(queryClient);
      const optimisticReadAt = new Date().toISOString();
      const wasUnread = optimisticallyMarkOneRead(queryClient, notificationId, optimisticReadAt, snapshot);
      return { ...snapshot, optimisticReadAt, wasUnread };
    },
    onError: (_error, notificationId, context) => {
      restoreOneNotificationCache(queryClient, notificationId, context);
    },
    onSettled: () => invalidateNotificationQueries(queryClient)
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsAPI.markAllRead,
    onMutate: async () => {
      await cancelNotificationQueries(queryClient);
      const snapshot = snapshotNotificationCache(queryClient);
      optimisticallyMarkAllRead(queryClient, new Date().toISOString());
      return snapshot;
    },
    onError: (_error, _variables, snapshot) => {
      restoreNotificationCache(queryClient, snapshot);
    },
    onSettled: () => invalidateNotificationQueries(queryClient)
  });
};
